/**
 * The per-model display layout (`deal-model-layout-v1`) that the server adds
 * beside each entry of `GET /api/deals-v2/properties/:propertyId/models` as
 * `data.models[].layout`. It is derived from the model source only: the
 * verdict tiers with their simple measure / threshold, the KPI rows, the
 * authored sections with a role per row, the inputs with a tier and binding,
 * the scenarios, the solver and the boolean checks.
 *
 * The field is additive and optional. The parser is lenient in one direction
 * only: any shape problem yields `undefined` (the lot page then shows today's
 * flat breakdown), and it never fails the model list.
 */

export const DEAL_MODEL_LAYOUT_CONTRACT = 'deal-model-layout-v1'

export const LAYOUT_COMPARATORS = ['>=', '>', '<=', '<', '==', '!='] as const
export type LayoutComparator = typeof LAYOUT_COMPARATORS[number]
export const LAYOUT_ROW_ROLES = ['summary', 'detail', 'check', 'internal'] as const
export type LayoutRowRole = typeof LAYOUT_ROW_ROLES[number]
export const LAYOUT_CONTROLS = ['money', 'number', 'percentage', 'toggle', 'select', 'date', 'readOnly'] as const
export type LayoutControl = typeof LAYOUT_CONTROLS[number]
export const LAYOUT_INPUT_TIERS = ['primary', 'assumption', 'target'] as const
export type LayoutInputTier = typeof LAYOUT_INPUT_TIERS[number]
export const LAYOUT_BINDINGS = ['fact', 'propertyOverride', 'userSetting', 'default'] as const
export type LayoutBinding = typeof LAYOUT_BINDINGS[number]

export interface DealModelLayoutTier {
  value: string
  measureOutputId: string | null
  comparator: LayoutComparator | null
  thresholdInputKey: string | null
  thresholdOutputId: string | null
}

export interface DealModelLayoutVerdict { outputId: string; key: string; tiers: DealModelLayoutTier[] }

export type DealModelLayoutItem =
  | { kind: 'row'; outputId: string; role: LayoutRowRole }
  | { kind: 'input'; inputKey: string }

export interface DealModelLayoutSection { key: string; label: string; description: string | null; items: DealModelLayoutItem[] }

export interface DealModelLayoutInput {
  key: string
  label: string
  description: string | null
  /** The authored kind (normally one of the presentation semantic types; an unknown kind is kept as text). */
  semanticType: { kind: string; nullable: boolean; enumValues: string[] | null }
  unit: string | null
  control: LayoutControl
  editable: boolean
  step: string | null
  helpText: string | null
  tier: LayoutInputTier
  binding: LayoutBinding
  factPath: string | null
  factUnit: string | null
  propertyOverrideKey: string | null
  userSettingKey: string | null
  /** The authored default exactly as transported (number, string, boolean or null). */
  defaultValue: unknown
  scenarioOverridden: boolean
  requiredForComputation: boolean
}

export interface DealModelLayoutScenario { id: string; key: string; label: string; description: string | null }

export interface DealModelLayoutSolver { id: string; key: string; label: string; resultPath: string; constraintOutputId: string | null }

export interface DealModelReturnTargetBinding {
  inputId: string
  inputKey: string
  overrideKey: string
  outputId: string
}

export interface DealModelReturnTargets {
  modeInputId: string
  modeInputKey: string
  modeOverrideKey: string
  candidateInputId: string
  candidateInputKey: string
  candidateOverrideKey: string | null
  margin?: DealModelReturnTargetBinding
  profit?: DealModelReturnTargetBinding
}

export interface DealModelLayoutCheck {
  outputId: string
  leftOutputId: string | null
  leftInputKey: string | null
  comparator: LayoutComparator | null
  rightOutputId: string | null
  rightInputKey: string | null
}

export interface DealModelLayout {
  contractVersion: typeof DEAL_MODEL_LAYOUT_CONTRACT
  verdict: DealModelLayoutVerdict | null
  kpiOutputIds: string[]
  sections: DealModelLayoutSection[]
  ungroupedOutputIds: string[]
  inputs: DealModelLayoutInput[]
  scenarios: DealModelLayoutScenario[]
  solver: DealModelLayoutSolver | null
  returnTargets?: DealModelReturnTargets
  checks: DealModelLayoutCheck[]
}

/* ---------------------------------------------------------------- parser */

class LayoutShapeError extends Error {}
type JsonRecord = Record<string, unknown>

const fail = (): never => { throw new LayoutShapeError('layout shape') }
const rec = (value: unknown): JsonRecord => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : fail()
const arr = (value: unknown): unknown[] => Array.isArray(value) ? value : fail()
const str = (value: unknown): string => typeof value === 'string' && value.length > 0 ? value : fail()
const text = (value: unknown): string => typeof value === 'string' ? value : fail()
/** Absent and null are the same "not declared" answer. */
const optStr = (value: unknown): string | null => value === null || value === undefined ? null : text(value)
const bool = (value: unknown): boolean => typeof value === 'boolean' ? value : fail()
const strings = (value: unknown): string[] => arr(value).map(str)
function oneOf<T extends string>(value: unknown, choices: readonly T[]): T {
  return typeof value === 'string' && (choices as readonly string[]).includes(value) ? value as T : fail()
}
function optOneOf<T extends string>(value: unknown, choices: readonly T[]): T | null {
  return value === null || value === undefined ? null : oneOf(value, choices)
}

function tier(value: unknown): DealModelLayoutTier {
  const row = rec(value)
  return {
    value: str(row.value),
    measureOutputId: optStr(row.measureOutputId),
    comparator: optOneOf(row.comparator, LAYOUT_COMPARATORS),
    thresholdInputKey: optStr(row.thresholdInputKey),
    thresholdOutputId: optStr(row.thresholdOutputId),
  }
}

function item(value: unknown): DealModelLayoutItem {
  const row = rec(value)
  if (row.kind === 'row') return { kind: 'row', outputId: str(row.outputId), role: oneOf(row.role, LAYOUT_ROW_ROLES) }
  if (row.kind === 'input') return { kind: 'input', inputKey: str(row.inputKey) }
  return fail()
}

function input(value: unknown): DealModelLayoutInput {
  const row = rec(value)
  const semantic = rec(row.semanticType)
  const enumValues = semantic.enumValues === null || semantic.enumValues === undefined ? null : strings(semantic.enumValues)
  const step = row.step === null || row.step === undefined ? null : typeof row.step === 'number' && Number.isFinite(row.step) ? String(row.step) : text(row.step)
  return {
    key: str(row.key),
    label: text(row.label),
    description: optStr(row.description),
    semanticType: { kind: str(semantic.kind), nullable: bool(semantic.nullable), enumValues },
    unit: optStr(row.unit),
    control: oneOf(row.control, LAYOUT_CONTROLS),
    editable: bool(row.editable),
    step,
    helpText: optStr(row.helpText),
    tier: oneOf(row.tier, LAYOUT_INPUT_TIERS),
    binding: oneOf(row.binding, LAYOUT_BINDINGS),
    factPath: optStr(row.factPath),
    factUnit: optStr(row.factUnit),
    propertyOverrideKey: optStr(row.propertyOverrideKey),
    userSettingKey: optStr(row.userSettingKey),
    defaultValue: row.defaultValue === undefined ? null : row.defaultValue,
    scenarioOverridden: bool(row.scenarioOverridden),
    requiredForComputation: bool(row.requiredForComputation),
  }
}

function check(value: unknown): DealModelLayoutCheck {
  const row = rec(value)
  return {
    outputId: str(row.outputId),
    leftOutputId: optStr(row.leftOutputId),
    leftInputKey: optStr(row.leftInputKey),
    comparator: optOneOf(row.comparator, LAYOUT_COMPARATORS),
    rightOutputId: optStr(row.rightOutputId),
    rightInputKey: optStr(row.rightInputKey),
  }
}

function returnTargets(value: unknown, inputs: DealModelLayoutInput[]): DealModelReturnTargets | undefined {
  if (value === null || value === undefined) return undefined
  const row = rec(value)
  const metric = (value: unknown, kind: 'ratio' | 'money'): DealModelReturnTargetBinding | undefined => {
    if (value === null || value === undefined) return undefined
    const binding = rec(value)
    const result = { inputId: str(binding.inputId), inputKey: str(binding.inputKey), overrideKey: str(binding.overrideKey), outputId: str(binding.outputId) }
    if (!inputs.some(input => input.key === result.inputKey && input.propertyOverrideKey === result.overrideKey && input.semanticType.kind === kind)) fail()
    return result
  }
  const result: DealModelReturnTargets = {
    modeInputId: str(row.modeInputId), modeInputKey: str(row.modeInputKey), modeOverrideKey: str(row.modeOverrideKey),
    candidateInputId: str(row.candidateInputId), candidateInputKey: str(row.candidateInputKey), candidateOverrideKey: optStr(row.candidateOverrideKey),
    margin: metric(row.margin, 'ratio'), profit: metric(row.profit, 'money'),
  }
  if (!result.margin && !result.profit) fail()
  if (!inputs.some(input => input.key === result.modeInputKey && input.propertyOverrideKey === result.modeOverrideKey && input.semanticType.kind === 'enum')) fail()
  if (!inputs.some(input => input.key === result.candidateInputKey && input.propertyOverrideKey === result.candidateOverrideKey)) fail()
  const keys = [result.modeOverrideKey, result.margin?.overrideKey, result.profit?.overrideKey].filter((key): key is string => key !== undefined)
  if (new Set(keys).size !== keys.length || (result.candidateOverrideKey && keys.includes(result.candidateOverrideKey))) fail()
  return result
}

function parseStrict(value: unknown): DealModelLayout {
  const root = rec(value)
  if (root.contractVersion !== DEAL_MODEL_LAYOUT_CONTRACT) fail()
  const verdictRow = root.verdict === null || root.verdict === undefined ? null : rec(root.verdict)
  const verdict = verdictRow ? { outputId: str(verdictRow.outputId), key: str(verdictRow.key), tiers: arr(verdictRow.tiers).map(tier) } : null
  const solverRow = root.solver === null || root.solver === undefined ? null : rec(root.solver)
  const solver = solverRow ? { id: str(solverRow.id), key: str(solverRow.key), label: text(solverRow.label), resultPath: str(solverRow.resultPath), constraintOutputId: optStr(solverRow.constraintOutputId) } : null
  const inputs = arr(root.inputs).map(input)
  if (new Set(inputs.map(entry => entry.key)).size !== inputs.length) fail()
  return {
    contractVersion: DEAL_MODEL_LAYOUT_CONTRACT,
    verdict,
    kpiOutputIds: strings(root.kpiOutputIds),
    sections: arr(root.sections).map(section => {
      const row = rec(section)
      return { key: str(row.key), label: text(row.label), description: optStr(row.description), items: arr(row.items).map(item) }
    }),
    ungroupedOutputIds: strings(root.ungroupedOutputIds),
    inputs,
    scenarios: arr(root.scenarios).map(scenario => {
      const row = rec(scenario)
      return { id: str(row.id), key: str(row.key), label: text(row.label), description: optStr(row.description) }
    }),
    solver,
    returnTargets: returnTargets(root.returnTargets, inputs),
    checks: arr(root.checks).map(check),
  }
}

/** The layout, or `undefined` when it is absent (an older server) or has any shape problem. */
export function parseDealModelLayout(value: unknown): DealModelLayout | undefined {
  if (value === null || value === undefined) return undefined
  // Any failure, including an unexpected one, degrades to the flat breakdown.
  try { return parseStrict(value) } catch { return undefined }
}

/** Replace each model's raw `layout` with its parsed form; the list itself is never failed here. */
export function withParsedLayouts<T extends { layout?: unknown }>(models: T[]): Array<Omit<T, 'layout'> & { layout?: DealModelLayout }> {
  return models.map(model => {
    const raw = model && typeof model === 'object' ? model : null
    if (!raw) return model as Omit<T, 'layout'> & { layout?: DealModelLayout }
    const { layout, ...rest } = raw
    const parsed = parseDealModelLayout(layout)
    return parsed ? { ...rest, layout: parsed } : rest
  })
}
